import React, { useEffect, useState } from 'react'
import { UserCheck, Clock, CheckCircle, XCircle, Plus, Search, RefreshCw, Calendar } from 'lucide-react'
import AddVisitorModal from './AddVisitorModal'
import Modal from './Modal'
import { API_BASE } from '../config'
import { formatLocalizedDateTime, localDateString, localDateTimeString, parseDisplayTimestamp, toUtcIsoString } from '../utils/dateUtils'

function formatVisitorTime(val) {
  if (!val) return ''
  const date = parseDisplayTimestamp(val)
  if (!date) return ''
  return formatLocalizedDateTime(date)
}

function getStatusBadge(status) {
  const badges = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-blue-100 text-blue-800',
    rejected: 'bg-red-100 text-red-800',
    'checked-in': 'bg-green-100 text-green-800',
    'checked-out': 'bg-gray-100 text-gray-800'
  }
  if (!status) status = 'pending'
  return (
    <span className={`px-2 py-1 rounded-full text-xs ${badges[status]}`}>{status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}</span>
  )
}

export default function VisitorManagement({ onAddVisitor, initialFilter, trialMaxPreApproveDay, isTrial }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState(initialFilter === 'checked-in' ? 'checked-in' : initialFilter === 'pending' ? 'pending' : 'all')
  // When navigating from dashboard, clear date filter to show all visitors (not just today)
  const [dateFilter, setDateFilter] = useState(initialFilter ? '' : localDateString())
  const [visitors, setVisitors] = useState([])
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [toasts, setToasts] = useState([])
  const [pendingReject, setPendingReject] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newUnit, setNewUnit] = useState('')
  const [newPurpose, setNewPurpose] = useState('')
  const [newExpected, setNewExpected] = useState('')

  // decode id_token to extract email
  function decodeIdTokenEmail() {
    try {
      const t = sessionStorage.getItem('id_token')
      if (!t) return null
      const payload = t.split('.')[1]
      const json = decodeURIComponent(atob(payload.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      const p = JSON.parse(json)
      return (p.email || '').toString().toLowerCase()
    } catch (e) {
      console.warn('failed to decode id_token', e)
      return null
    }
  }

  async function fetchUserRecord(email) {
    try {
      const token = sessionStorage.getItem('id_token')
      const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) return null
      const data = await resp.json()
      // Multi-community: pick the community the user selected (stored in sessionStorage)
      if (data.multiCommunity && Array.isArray(data.communities)) {
        const selectedCid = sessionStorage.getItem('community_id') || ''
        const selectedUid = sessionStorage.getItem('user_id') || ''
        return data.communities.find(c => {
          const cid = c.COMMUNITY_ID || c.communityId || ''
          const uid = c.USER_ID || c.userId || ''
          return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid)
        }) || data.communities[0] || null
      }
      return data
    } catch (e) {
      console.error('fetchUserRecord error', e)
      return null
    }
  }

  function mapApiVisitor(it) {
    // API returns uppercase keys; normalize to UI model
    return {
      id: it.VISITOR_ID || it.visitorId || it.VISIT_ID,
      name: it.NAME || it.name || '',
      visitingUnit: it.UNIT_NBR || it.unit || it.unitNbr || '',
      purpose: it.VISIT_PURPS || it.purpose || it.PURPOSE || '',
      phone: it.PHONE || it.phone || '',
      expectedTime: it.EXPECTED_TIME || it.expectedTime || '',
      checkInTime: it.ENTRY_TIME || it.entryTime || it.ENTRY_TIME,
      checkOutTime: it.EXIT_TIME || it.exitTime || it.EXIT_TIME,
      status: (it.STATUS || it.status || 'pending').toString().toLowerCase(),
      communityId: it.COMMUNITY_ID || it.communityId || it.COMM_VISTRS_PRTN_KEY_ID || ''
    }
  }

  async function load() {
    setLoading(true)
    const email = decodeIdTokenEmail()
    let userRecord = null
    if (email) {
      userRecord = await fetchUserRecord(email)
      setUser(userRecord)
    }

    // prepare visitors fetch
    const token = sessionStorage.getItem('id_token')
    let url = `${API_BASE}/visitors`
    const params = new URLSearchParams()
    if (userRecord && userRecord.ROLE && userRecord.ROLE.toLowerCase() !== 'admin') {
      // resident - filter by unit
      const unit = userRecord.UNIT_NBR || userRecord.unitNbr || userRecord.unit_nbr || userRecord.unit
      if (unit) params.set('unitNbr', unit)
      // also pass communityId so date GSI can be used
      const community = userRecord.COMMUNITY_ID || userRecord.communityId || userRecord.community_id || userRecord.COMM_VISTRS_PRTN_KEY_ID
      if (community) params.set('communityId', community)
    } else if (userRecord && userRecord.ROLE && userRecord.ROLE.toLowerCase() === 'admin') {
      // admin - restrict to community
      const community = userRecord.COMMUNITY_ID || userRecord.communityId || userRecord.community_id || userRecord.COMM_VISTRS_PRTN_KEY_ID
      if (community) params.set('communityId', community)
    }
    // Add date filter for server-side filtering
    if (dateFilter) params.set('date', dateFilter)
    if (params.toString()) url += `?${params.toString()}`

    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) {
        console.error('failed to fetch visitors', resp.status)
        setVisitors([])
        setLoading(false)
        return
      }
      const data = await resp.json()
      const mapped = (Array.isArray(data) ? data : []).map(mapApiVisitor)
      setVisitors(mapped)
    } catch (e) {
      console.error('load visitors error', e)
      setVisitors([])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [dateFilter])

  // update helper - calls PATCH endpoint
  async function updateVisitor(communityId, visitorId, payload) {
    const token = sessionStorage.getItem('id_token')
    try {
      const resp = await fetch(`${API_BASE}/visitors`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ COMMUNITY_ID: communityId, VISITOR_ID: visitorId, ...payload })
      })
      if (!resp.ok) throw new Error('update failed')
      const updated = await resp.json()
      // Merge returned attributes into existing visitor entry instead of replacing it.
      const patch = mapApiVisitor(updated)
      setVisitors((prev) => prev.map(v => (v.id === visitorId ? { ...v, ...patch } : v)))
      return true
    } catch (e) {
      console.error('updateVisitor error', e)
      return false
    }
  }

  function showToast(message, type = 'success') {
    const id = Date.now().toString() + Math.random().toString(36).slice(2, 7)
    setToasts((t) => [...t, { id, message, type }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500)
  }

  async function handleApprove(id) {
    const v = visitors.find(x => x.id === id)
    if (!v) return
    const unit = user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || ''
    const role = user?.ROLE || user?.role || ''
    const actionBy = `${unit}_${role}`
    const ok = await updateVisitor(v.communityId, id, { ACTION_BY: actionBy, STATUS: 'APPROVED' })
    if (ok) showToast('Visitor approved', 'success')
    else showToast('Failed to approve visitor', 'error')
  }

  async function handleReject(id) {
    const v = visitors.find(x => x.id === id)
    if (!v) return
    const unit = user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || ''
    const role = user?.ROLE || user?.role || ''
    const actionBy = `${unit}_${role}`
    const ok = await updateVisitor(v.communityId, id, { ACTION_BY: actionBy, STATUS: 'REJECTED' })
    if (ok) showToast('Visitor rejected', 'success')
    else showToast('Failed to reject visitor', 'error')
  }

  async function handleCheckIn(id) {
    const v = visitors.find(x => x.id === id)
    if (!v) return
    const now = toUtcIsoString(new Date())
    const unit = user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || ''
    const role = user?.ROLE || user?.role || ''
    const actionBy = `${unit}_${role}`
    const ok = await updateVisitor(v.communityId, id, { ACTION_BY: actionBy, ENTRY_TIME: now, STATUS: 'checked-in' })
    if (ok) showToast('Checked in', 'success')
    else showToast('Failed to check in', 'error')
  }

  async function handleCheckOut(id) {
    const v = visitors.find(x => x.id === id)
    if (!v) return
    const now = toUtcIsoString(new Date())
    const unit = user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || ''
    const role = user?.ROLE || user?.role || ''
    const actionBy = `${unit}_${role}`
    const ok = await updateVisitor(v.communityId, id, { ACTION_BY: actionBy, EXIT_TIME: now, STATUS: 'checked-out' })
    if (ok) showToast('Checked out', 'success')
    else showToast('Failed to check out', 'error')
  }

  const filteredVisitors = visitors.filter(visitor => {
    const matchesSearch = (visitor.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (visitor.visitingUnit || '').toLowerCase().includes(searchTerm.toLowerCase())
    let matchesFilter = false
    if (filterStatus === 'all') matchesFilter = true
    else matchesFilter = visitor.status === filterStatus
    return matchesSearch && matchesFilter
  })

  return (
    <div className="p-4">
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 safe-top safe-right">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-2 rounded shadow text-sm max-w-[90vw] ${t.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
            {t.message}
          </div>
        ))}
      </div>
      {/* Confirm Reject Modal */}
      {pendingReject && (
        <Modal title="Confirm Reject" onClose={() => setPendingReject(null)}>
          <p className="mb-4">Are you sure you want to reject visitor <strong>{pendingReject.name}</strong> from unit <strong>{pendingReject.visitingUnit}</strong>?</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => setPendingReject(null)} className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700">Cancel</button>
            <button onClick={async () => { const id = pendingReject.id; setPendingReject(null); await handleReject(id); }} className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white">Reject</button>
          </div>
        </Modal>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-lg font-bold">Visitor Management</h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-gray-500 rounded-lg bg-gray-100 active:bg-gray-200"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => { setShowAddModal(true); if (onAddVisitor) onAddVisitor(); }} className="bg-blue-600 text-white p-2 rounded-lg flex-1 sm:flex-none sm:px-3 sm:py-2 flex items-center justify-center gap-2"><Plus size={20} /><span className="sm:hidden text-sm font-medium">Add Visitor</span></button>
        </div>
      </div>

      {/* Trial pre-approval limit banner */}
      {(() => {
        // -1 (or any negative) means "unlimited" — used by PAID plans; only TRIAL plans enforce a real limit.
        if (isTrial === false || trialMaxPreApproveDay == null || trialMaxPreApproveDay < 0) return null
        const today = localDateString()
        const todayCount = visitors.filter(v => {
          const vDate = (v.expectedTime || '').slice(0, 10)
          return vDate === today && ['pending', 'approved'].includes(v.status)
        }).length
        const atLimit = todayCount >= trialMaxPreApproveDay
        return (
          <div style={{
            background: atLimit ? '#fef2f2' : '#fffbeb',
            border: `1px solid ${atLimit ? '#fca5a5' : '#fbbf24'}`,
            borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 8,
            color: atLimit ? '#991b1b' : '#92400e'
          }}>
            {atLimit
              ? `Trial limit: ${trialMaxPreApproveDay} pre-approvals/day reached. Upgrade to add more.`
              : `Trial: ${todayCount} / ${trialMaxPreApproveDay} pre-approvals used today`}
          </div>
        )
      })()}

      <AddVisitorModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSubmit={async (formData) => {
        console.debug('VisitorManagement: AddVisitor onSubmit', formData)
        const token = sessionStorage.getItem('id_token')
        const community = user?.COMMUNITY_ID || user?.communityId || user?.COMM_VISTRS_PRTN_KEY_ID || ''
        if (!community) { showToast('Missing community id', 'error'); return }
        const expected = formData.expectedTime || localDateTimeString()
        const body = {
          COMMUNITY_ID: community,
          UNIT_NBR: formData.visitingUnit,
          EXPECTED_TIME: expected,
          VISIT_PURPS: formData.purpose,
          PHONE: formData.phone || '',
          NAME: formData.name
        }
        try {
          const resp = await fetch(`${API_BASE}/visitors`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) })
          if (!resp.ok) throw new Error('create failed')
          showToast('Visitor created', 'success')
          setShowAddModal(false)
          await load()
        } catch (e) {
          console.error('create visitor error', e)
          showToast('Failed to create visitor', 'error')
        }
      }} />

      {/* Date Filter */}
      <div className="relative mb-3">
        <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="w-full block box-border border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm bg-white shadow-sm appearance-none"
          style={{ WebkitAppearance: 'none', minWidth: 0 }}
        />
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input type="text" placeholder="Search visitors..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-white shadow-sm" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4 sm:flex sm:flex-wrap sm:gap-2">
        {['all','pending','approved','checked-in'].map((status)=> {
          const statusLabel = status === 'checked-in' ? 'Checked in' : status.charAt(0).toUpperCase() + status.slice(1)
          return (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`w-full sm:w-auto px-2.5 sm:px-3 py-2 rounded-lg text-[13px] sm:text-sm leading-tight text-center whitespace-nowrap ${filterStatus===status ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              {statusLabel}
            </button>
          )
        })}
      </div>

      <div className="space-y-3">
        {loading && <div className="text-gray-500 text-center py-4 text-sm">Loading visitors…</div>}
        {!loading && filteredVisitors.length === 0 && (
                <div className="text-white font-medium text-center py-8">No visitors found for selected date</div>
              )}
        {!loading && filteredVisitors.map(visitor => (
          <div key={visitor.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="mb-1">{visitor.name}</h3>
                <p className="text-gray-600 text-sm">{visitor.phone}</p>
              </div>
              {getStatusBadge(visitor.status)}
            </div>

            <div className="space-y-1 mb-3">
              <p className="text-sm text-gray-600"><span className="text-gray-900">Unit:</span> {visitor.visitingUnit}</p>
              <p className="text-sm text-gray-600"><span className="text-gray-900">Purpose:</span> {visitor.purpose}</p>
              <p className="text-sm text-gray-600"><span className="text-gray-900">Expected:</span> {formatVisitorTime(visitor.expectedTime)}</p>
              {visitor.checkInTime && (<p className="text-sm text-gray-600"><span className="text-gray-900">Check-in:</span> {formatVisitorTime(visitor.checkInTime)}</p>)}
              {visitor.checkOutTime && (<p className="text-sm text-gray-600"><span className="text-gray-900">Check-out:</span> {formatVisitorTime(visitor.checkOutTime)}</p>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {/* Button logic: if ENTRY_TIME and EXIT_TIME exist -> no buttons */}
              {(!visitor.checkInTime && !visitor.checkOutTime) && visitor.status === 'pending' && (
                <>
                  <button onClick={() => handleApprove(visitor.id)} className="flex-1 bg-green-600 text-white py-2 rounded-lg flex items-center justify-center gap-2"><CheckCircle size={18} /><span>Approve</span></button>
                  <button onClick={() => handleReject(visitor.id)} className="flex-1 bg-red-600 text-white py-2 rounded-lg flex items-center justify-center gap-2"><XCircle size={18} /><span>Reject</span></button>
                </>
              )}

              {(!visitor.checkInTime && !visitor.checkOutTime) && visitor.status === 'approved' && (
                <button onClick={() => handleCheckIn(visitor.id)} className="w-full bg-blue-600 text-white py-2 rounded-lg flex items-center justify-center gap-2"><UserCheck size={18} /><span>Check In</span></button>
              )}

              {(visitor.checkInTime && !visitor.checkOutTime) && (
                <button onClick={() => handleCheckOut(visitor.id)} className="w-full bg-gray-600 text-white py-2 rounded-lg flex items-center justify-center gap-2"><Clock size={18} /><span>Check Out</span></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
